import React, { useEffect, useRef, useState } from 'react';

import { createProductsImagesMulti } from '@/api/products/createProductsImages';
import LoadingComponent from '@/components/Common/LoadingComponent';
import AlertModal from '@/components/Common/Modal/AlertModal';
import { InformationCircleIcon, PlusSmallIcon, XMarkIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { useToastContext } from '@/contexts/Common/ToastContext';

const MAX_DOCUMENT_COUNT = 5;
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];

interface UploadedDocument {
  url: string;
  name: string;
}

interface EvidentiaryDocumentsProps {
  productInfo: ProductDetailResponse;
  setProductInfo: React.Dispatch<React.SetStateAction<ProductDetailResponse>>;
  updateDisabledBtn?: (value: boolean) => void;
}

const WarningTriangleIcon = () => (
  <svg width={20} height={20} viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill={colors.red}
      d="M13.902 4.5c1.155-2 4.041-2 5.196 0l9.093 15.75c1.155 2-.288 4.5-2.598 4.5H7.407c-2.31 0-3.753-2.5-2.598-4.5L13.902 4.5Z"
    />
    <path
      fill="#ffffff"
      d="M17.685 17.723h-2.37L15.067 9.5h2.867l-.249 8.223ZM16.492 21.5a1.51 1.51 0 0 1-.754-.196A1.435 1.435 0 0 1 15 20.012a1.343 1.343 0 0 1 .19-.727 1.51 1.51 0 0 1 1.301-.727c.26 0 .504.065.73.195.232.131.417.308.555.532.144.223.218.466.224.727a1.489 1.489 0 0 1-.215.752 1.586 1.586 0 0 1-.556.54 1.48 1.48 0 0 1-.737.196Z"
    />
  </svg>
);

const buildUrlFields = (docs: UploadedDocument[]) => ({
  evidentiaryDocumentsUrl1: docs[0]?.url ?? '',
  evidentiaryDocumentsUrl2: docs[1]?.url ?? '',
  evidentiaryDocumentsUrl3: docs[2]?.url ?? '',
  evidentiaryDocumentsUrl4: docs[3]?.url ?? '',
  evidentiaryDocumentsUrl5: docs[4]?.url ?? '',
});

const EvidentiaryDocuments: React.FC<EvidentiaryDocumentsProps> = ({ productInfo, setProductInfo, updateDisabledBtn }) => {
  const { showToast } = useToastContext();
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isShowInvalidModal, setIsShowInvalidModal] = useState(false);
  const isInitializedRef = useRef(false);

  // 기존에 등록된 증빙서류를 최초 1회 불러온다.
  useEffect(() => {
    if (isInitializedRef.current) return;
    const urls = [
      productInfo?.productsImage?.evidentiaryDocumentsUrl1,
      productInfo?.productsImage?.evidentiaryDocumentsUrl2,
      productInfo?.productsImage?.evidentiaryDocumentsUrl3,
      productInfo?.productsImage?.evidentiaryDocumentsUrl4,
      productInfo?.productsImage?.evidentiaryDocumentsUrl5,
    ].filter((url): url is string => !!url);
    if (urls.length > 0) {
      setDocuments(urls.map((url, index) => ({ url, name: `증빙서류 ${index + 1}` })));
    }
    isInitializedRef.current = true;
  }, [productInfo]);

  // 증빙서류는 선택 입력이므로 진입 시 저장 버튼을 활성화한다.
  useEffect(() => {
    updateDisabledBtn?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyDocuments = (nextDocuments: UploadedDocument[]) => {
    const limited = nextDocuments.slice(0, MAX_DOCUMENT_COUNT);
    setDocuments(limited);
    // 증빙서류 URL은 productsImage 객체 안에서 관리된다.
    setProductInfo((prev) => ({ ...prev, productsImage: { ...prev.productsImage, ...buildUrlFields(limited) } }));
  };

  const openFileDialog = () => {
    if (isUploading) return;
    const input = document.getElementById('edit-authority-documents') as HTMLInputElement;
    input?.click();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const filesArray = Array.from(e.target.files);
    e.target.value = '';

    const hasInvalidFile = filesArray.some((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return !ext || !ALLOWED_EXTENSIONS.includes(ext);
    });
    if (hasInvalidFile) {
      setIsShowInvalidModal(true);
      return;
    }

    const remaining = MAX_DOCUMENT_COUNT - documents.length;
    if (remaining <= 0) return;
    const targetFiles = filesArray.slice(0, remaining);

    const formData = new FormData();
    targetFiles.forEach((file) => formData.append('uploadFile', file));
    formData.append('truckNumber', productInfo?.truckNumber || '');

    try {
      setIsUploading(true);
      const response = await createProductsImagesMulti(formData);
      const uploaded = (response.data || []) as { url: string }[];
      const newDocs: UploadedDocument[] = uploaded
        .map((item, index) => ({
          url: item?.url || '',
          name: targetFiles[index]?.name || '증빙서류',
        }))
        .filter((doc) => !!doc.url);
      applyDocuments([...documents, ...newDocs]);
    } catch {
      showToast('서류 업로드에 문제가 생겼어요.', 'error', 'bottom');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (targetUrl: string) => {
    applyDocuments(documents.filter((doc) => doc.url !== targetUrl));
  };

  return (
    <div className="flex flex-col w-full">
      <div className="rounded-[8px] border border-[#F6C9C9] bg-[#FEF3F3] p-4">
        <div className="flex items-center gap-1.5">
          <WarningTriangleIcon />
          <span className="text-[15px] font-bold text-red">서류 제출 후 판매 가능</span>
        </div>
        <p className="mt-1.5 text-[13px] leading-[18px] text-[#DE7B7B] break-keep">
          소유주와 인증 이름이 다릅니다. 판매 권한 증빙 서류 제출시 관리자 검토 후 등록됩니다. (영업일 1~3일 소요)
        </p>
      </div>

      <div className="mt-5 rounded-[8px] border border-gray-3 bg-white p-4">
        <p className="text-[15px] font-bold text-gray-8">필요 서류 안내</p>
        <p className="mt-1 text-[13px] leading-[18px] text-gray-6">
          아래 중 해당하는 서류를 1개 이상 첨부하세요.
        </p>
        <ul className="mt-3 flex flex-col gap-2.5">
          {[
            '위수탁계약서(지입 넘버일 경우, 타인 명의 위수탁 일경우 판매위임장 까지 첨부)',
            '판매위임장(타인의 차량을 판매할경우)',
            '가족관계증명서(가족차량을 판매할경우)',
            '법인재직증명서(법인차량)',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2 text-[14px] leading-[20px] text-gray-8 break-keep">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary-10" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2">
        {documents.map((doc) => (
          <div key={doc.url} className="relative">
            <XMarkIcon
              className="absolute z-10 top-1 right-1 cursor-pointer"
              color={colors.gray[0]}
              style={{ filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.7))' }}
              onClick={() => handleDelete(doc.url)}
            />
            <img src={doc.url} alt={doc.name} className="h-[104px] w-full rounded-lg object-cover" />
          </div>
        ))}

        {documents.length < MAX_DOCUMENT_COUNT && (
          <div
            className="flex h-[104px] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-4 bg-gray-1 text-gray-6"
            onClick={openFileDialog}
          >
            {isUploading ? (
              <LoadingComponent size={28} margin={0} />
            ) : (
              <>
                <PlusSmallIcon color={colors.gray[5]} width={30} height={30} />
                <span className="text-[13px] text-gray-6">추가</span>
                <span className="text-[13px] text-gray-5">
                  {documents.length}/{MAX_DOCUMENT_COUNT}
                </span>
              </>
            )}
            <input
              id="edit-authority-documents"
              className="hidden"
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex items-start gap-1 text-[13px] leading-[18px] text-gray-6">
        <span className="mt-[1px] shrink-0">
          <InformationCircleIcon color={colors.gray[5]} width={16} height={16} />
        </span>
        <span>서류 검토는 영업일 기준 1~3일 소요됩니다.</span>
      </div>

      <AlertModal
        isOpen={isShowInvalidModal}
        onClose={() => setIsShowInvalidModal(false)}
        content={
          <span>
            서류는 png, jpg, jpeg 확장자만
            <br />
            등록 가능합니다.
          </span>
        }
        buttonLabel="확인"
      />
    </div>
  );
};

export default EvidentiaryDocuments;
